'use client'

import type { ReactNode } from 'react'

import type { NotificationView } from '@package/notification'
import { renderNotificationCopy } from '@package/notification'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

export type NotificationCardLayout = 'media-below' | 'media-left' | 'text-only'

interface NotificationCardProps {
  notification: NotificationView
  layout?: NotificationCardLayout
  leading?: ReactNode
  /**
   * Which context the card belongs to, drawn beside the timestamp — the family organization's
   * name on a guardian card. The feed is per user and mixes contexts (a teacher who is also a
   * parent reads school cards and home cards in one list), and a card that opens somewhere other
   * than the app the reader is standing in has to say so before it is clicked.
   */
  contextLabel?: string
  /** Extra content below the body — used by ActorNotificationCard for read progress. */
  children?: ReactNode
  onSelect?: () => void
  className?: string
}

/**
 * Covers are not interchangeable: a book is 3:4, a curated `BOOK_COLLECTION` is 5:4, and a
 * lesson module (challenge/exercise) is 4:3 — the same ratios `CoverPreview` uses in the
 * share dialogs. A user-authored `COLLECTION` has no cover of its own and is square: the
 * 2x2 gallery of its member books, matching `/pilihanku/koleksiku/$id` and the share dialog.
 * Height is fixed per layout and the width follows the ratio, so a feed of mixed shares
 * keeps one vertical rhythm.
 */
/** Portrait book cover — also the fallback, since an unknown type is most likely a book. */
const BOOK_COVER = { aspectClass: 'srd:aspect-3/4', ratio: 3 / 4 }

const COVER_BY_ENTITY: Record<string, { aspectClass: string; ratio: number }> = {
  BOOK: BOOK_COVER,
  COLLECTION: { aspectClass: 'srd:aspect-square', ratio: 1 },
  BOOK_COLLECTION: { aspectClass: 'srd:aspect-5/4', ratio: 5 / 4 },
  CHALLENGE: { aspectClass: 'srd:aspect-4/3', ratio: 4 / 3 },
  EXERCISE: { aspectClass: 'srd:aspect-4/3', ratio: 4 / 3 },
  // A course's artwork is its lesson module's cover, so it takes the lesson ratio.
  COURSE: { aspectClass: 'srd:aspect-4/3', ratio: 4 / 3 },
}

function coverSizing(entityType: string | undefined, height: number) {
  const { aspectClass, ratio } = COVER_BY_ENTITY[entityType ?? 'BOOK'] ?? BOOK_COVER
  // The width attribute only exists to reserve the right box before the image loads.
  return { aspectClass, width: Math.round(height * ratio), height }
}

/** Tiles in the gallery grid — same cap as `BookGalleryCard`. */
const MAX_GALLERY_COVERS = 4

/**
 * A user-authored `COLLECTION` cannot be drawn as one `<img>`: its artwork is the 2x2 grid
 * of member covers that `BookGalleryCard` renders on `/pilihanku/koleksiku/$id` and in the
 * share dialog, and the feed has to show the same thing. Rows emitted before the gallery
 * snapshot existed carry only the first member cover — that renders as a single tile in the
 * same square grid, which is exactly how the collection page draws a one-book collection.
 */
function NotificationCover({
  notification,
  cover,
  className,
}: {
  notification: NotificationView
  cover: { aspectClass: string; width: number; height: number }
  className?: string
}) {
  const { thumbnailUrl, thumbnailUrls } = notification.snapshot

  if (notification.entity?.type === 'COLLECTION') {
    const urls = (thumbnailUrls?.length ? thumbnailUrls : thumbnailUrl ? [thumbnailUrl] : []).slice(
      0,
      MAX_GALLERY_COVERS,
    )
    if (urls.length === 0) return null

    return (
      <div
        className={cn(
          'srd:bg-blue-20 srd:border-grey-20 srd:grid srd:w-auto srd:grid-cols-2 srd:grid-rows-2 srd:overflow-clip srd:rounded-lg srd:border',
          cover.aspectClass,
          className,
        )}
      >
        {urls.map((url) => (
          <img key={url} src={url} alt='' draggable={false} className='srd:h-full srd:w-full srd:object-cover' />
        ))}
      </div>
    )
  }

  if (!thumbnailUrl) return null

  return (
    <img
      src={thumbnailUrl}
      alt=''
      width={cover.width}
      height={cover.height}
      className={cn('srd:w-auto srd:rounded-lg srd:object-cover', cover.aspectClass, className)}
    />
  )
}

function formatTimestamp(date: Date): string {
  return new Intl.DateTimeFormat('id-ID', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

/**
 * Shared shell for every notification card. Copy comes from the isomorphic
 * registry, never from the server, so the sentence and the push text stay in sync.
 */
export function NotificationCard({
  notification,
  layout = 'media-below',
  leading,
  contextLabel,
  children,
  onSelect,
  className,
}: NotificationCardProps) {
  const copy = renderNotificationCopy(notification)
  const isInert = !onSelect
  const cover = coverSizing(notification.entity?.type, layout === 'media-left' ? 64 : 80)

  return (
    <article
      className={cn(
        'srd:bg-neutral-white srd:relative srd:flex srd:w-full srd:gap-x-3 srd:rounded-xl srd:p-4',
        layout === 'media-left' ? 'srd:flex-row srd:items-center' : 'srd:flex-col',
        !isInert && 'srd:cursor-pointer',
        className,
      )}
      onClick={onSelect}
      role={isInert ? undefined : 'button'}
      tabIndex={isInert ? undefined : 0}
      onKeyDown={(event) => {
        if (!onSelect) return
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onSelect()
        }
      }}
    >
      {layout === 'media-left' && (
        <NotificationCover notification={notification} cover={cover} className='srd:h-16 srd:shrink-0' />
      )}

      <div className='srd:flex srd:w-full srd:flex-col srd:gap-y-1'>
        <div className='srd:flex srd:items-start srd:gap-x-2'>
          {leading}

          {/* The cover belongs to this column, not to the card: it must align with the
              sentence above it, indented past the avatar, so text + cover read as one block. */}
          <div className='srd:flex srd:w-full srd:flex-col'>
            <p className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:text-grey-100')}>
              <time>{formatTimestamp(notification.createdAt)}</time>
              {contextLabel && (
                <>
                  <span aria-hidden> · </span>
                  <span>{contextLabel}</span>
                </>
              )}
            </p>

            <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black')}>
              {copy.body.map((segment, index) => (
                <span key={index} className={segment.bold ? 'srd:font-semibold' : undefined}>
                  {segment.text}
                </span>
              ))}
            </p>

            {copy.quotedLine && (
              <p
                className={cn(
                  typographyVariants({ variant: 'body-sm' }),
                  // `whitespace-pre-line`: the teacher typed this into a textarea, so their line
                  // breaks are part of the message.
                  'srd:text-grey-100 srd:mt-1 srd:italic srd:whitespace-pre-line',
                )}
              >
                {copy.quotedLine}
              </p>
            )}

            {copy.contextLine && (
              <p className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:text-blue-70')}>{copy.contextLine}</p>
            )}

            {children}

            {layout === 'media-below' && (
              <NotificationCover
                notification={notification}
                cover={cover}
                // `self-start`, or the column's default stretch overrides the aspect ratio
                // and the cover spans the whole card width.
                className='srd:mt-2 srd:h-20 srd:self-start'
              />
            )}
          </div>
        </div>
      </div>

      {notification.isUnread && (
        <span
          className='srd:bg-yellow-100 srd:absolute srd:top-4 srd:right-4 srd:size-2 srd:rounded-full'
          aria-label='Belum dibaca'
        />
      )}
    </article>
  )
}
